import { useEffect, useState } from "react";
import { Modal } from "antd";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import SwitchComponent from "../../../components/switch";
import {
	createPromptConfigSkill,
	updatePromptConfigSkill,
} from "../../../services/prompt-ai";
import { buildAgentNavigation } from "../../../consts/agent-nav-config";
import { useAgentConfigSource } from "../../../helpers/hooks/agent-config-source";
import SubAgentMissing from "../sub-agent-scope/sub-agent-missing";

// The runtime calls a skill as `skill_<name>`, so the name must match the
// slug rule the backend validates against.
const NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

const emptyForm = () => ({
	name: "",
	description: "",
	body: "",
	allowedTools: "",
	disableModelInvocation: false,
});

// ─── SkillCard ────────────────────────────────────────────────────────────────
function SkillCard({ skill, onEdit, onDelete }) {
	return (
		<div className="flex flex-row items-start justify-between border border-gray-200 rounded-lg bg-white px-4 py-3 hover:shadow-sm transition-shadow">
			<div className="flex-1 min-w-0 pr-4">
				<div className="flex items-center gap-2">
					<p className="text-sm font-semibold text-gray-800 font-mono truncate">
						{skill.name}
					</p>
					{skill.disableModelInvocation && (
						<span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
							Manual only
						</span>
					)}
				</div>
				{skill.description && (
					<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{skill.description}</p>
				)}
				<p className="text-xs text-gray-400 mt-1">
					{(skill.body || "").length} characters of instructions
				</p>
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				<button
					type="button"
					onClick={() => onEdit(skill)}
					className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded"
					title="Edit skill"
				>
					<HiOutlinePencil className="h-4 w-4" />
				</button>
				<button
					type="button"
					onClick={() => onDelete(skill)}
					className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded"
					title="Delete skill"
				>
					<HiOutlineTrash className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────
function DeleteConfirmModal({ skill, onConfirm, onCancel, processing }) {
	return (
		<Modal
			title={<span className="font-semibold text-gray-800">Delete Skill</span>}
			open={!!skill}
			onCancel={onCancel}
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onCancel} disabled={processing} />
					<Button text="Delete" color="error" onClick={onConfirm} loading={processing} />
				</div>
			}
		>
			<p className="text-sm text-gray-600 mt-2">
				Are you sure you want to delete{" "}
				<span className="font-mono font-semibold">{skill?.name}</span>? This action cannot be undone.
			</p>
		</Modal>
	);
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AgentSkills() {
	const {
		appId,
		subAgentId,
		subAgent,
		isSubAgent,
		scopeLoading,
		scopeError,
		skills: skillsSource,
	} = useAgentConfigSource();

	const [skills, setSkills] = useState([]);
	const [loadState, setLoadState] = useState({ processing: false });

	const [modalOpen, setModalOpen] = useState(false);
	const [editingSkill, setEditingSkill] = useState(null);
	const [editingForm, setEditingForm] = useState(emptyForm());

	const [deletingSkill, setDeletingSkill] = useState(null);
	const [deleteProcessing, setDeleteProcessing] = useState(false);

	async function fetchSkills() {
		setLoadState({ processing: true });
		setSkills(await skillsSource.list());
		setLoadState({ processing: false });
	}

	useEffect(() => {
		// in sub-agent scope the record must land before its toolData can be read
		if (appId && !scopeLoading) fetchSkills();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [appId, subAgentId, scopeLoading]);

	const handleAddNew = () => {
		setEditingSkill(null);
		setEditingForm(emptyForm());
		setModalOpen(true);
	};

	const handleEdit = (skill) => {
		setEditingSkill(skill);
		setEditingForm({
			name: skill.name || "",
			description: skill.description || "",
			body: skill.body || "",
			allowedTools: (skill.allowedTools || []).join(", "),
			disableModelInvocation: !!skill.disableModelInvocation,
		});
		setModalOpen(true);
	};

	const handleSaved = () => {
		setModalOpen(false);
		setEditingSkill(null);
		fetchSkills();
	};

	const handleDeleteConfirm = async () => {
		if (!deletingSkill) return;
		setDeleteProcessing(true);
		await skillsSource.remove(deletingSkill.id);
		setDeleteProcessing(false);
		setDeletingSkill(null);
		fetchSkills();
	};

	const navigation = buildAgentNavigation({ subAgentId });

	if (isSubAgent && !scopeLoading && scopeError) {
		return <SubAgentMissing navigation={navigation} />;
	}

	return (
		<PageContainer
			title="Skills"
			subtitle={
				isSubAgent
					? `Skills ${subAgent?.name || "this sub-agent"} loads on demand`
					: "Reusable instruction modules the agent loads on demand"
			}
			navigationPosition="below-header"
			navigation={navigation}
			headerAction={<Button text="Add Skill" onClick={handleAddNew} />}
		>
			<div className="mt-6 px-1">
				{loadState.processing || scopeLoading ? (
					<div className="flex justify-center items-center py-16">
						<p className="text-sm text-gray-400">Loading skills…</p>
					</div>
				) : skills.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 text-center">
						<p className="text-gray-500 text-sm font-medium">No skill defined yet</p>
						<p className="text-gray-400 text-xs mt-1 mb-4">
							A skill holds detailed instructions the agent loads only when they are relevant.
						</p>
						<Button text="Add your first skill" onClick={handleAddNew} />
					</div>
				) : (
					<div className="flex flex-col gap-3">
						{skills.map((skill) => (
							<SkillCard
								key={skill.id}
								skill={skill}
								onEdit={handleEdit}
								onDelete={setDeletingSkill}
							/>
						))}
					</div>
				)}
			</div>

			<SkillModal
				open={modalOpen}
				skillId={editingSkill?.id}
				initialForm={editingForm}
				onClose={() => setModalOpen(false)}
				onSaved={handleSaved}
				appId={appId}
				onSave={isSubAgent ? skillsSource.save : undefined}
			/>

			<DeleteConfirmModal
				skill={deletingSkill}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeletingSkill(null)}
				processing={deleteProcessing}
			/>
		</PageContainer>
	);
}

// ─── SkillModal ───────────────────────────────────────────────────────────────
function SkillModal({ open, skillId, initialForm, onClose, onSaved, appId, onSave }) {
	const isEdit = !!skillId;

	const [form, setForm] = useState(initialForm || emptyForm());
	const [saveState, setSaveState] = useState({ processing: false, failed: false });
	const [error, setError] = useState("");

	useEffect(() => {
		if (open) {
			setForm(initialForm || emptyForm());
			setSaveState({ processing: false, failed: false });
			setError("");
		}
	}, [open, skillId]);

	const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

	const validate = () => {
		const name = form.name.trim();
		if (!name) return "Skill name is required.";
		if (!NAME_PATTERN.test(name))
			return "Name must be lowercase letters, numbers and hyphens (e.g. refund-policy).";
		if (!form.description.trim()) return "Description is required — it tells the agent when to use this skill.";
		if (!form.body.trim()) return "Instructions are required.";
		return "";
	};

	const handleSave = async () => {
		const validationError = validate();
		if (validationError) {
			setError(validationError);
			return;
		}
		setError("");
		setSaveState({ processing: true, failed: false });

		const payload = {
			name: form.name.trim(),
			description: form.description.trim(),
			body: form.body,
			allowedTools: form.allowedTools
				.split(",")
				.map((t) => t.trim())
				.filter(Boolean),
			disableModelInvocation: !!form.disableModelInvocation,
		};

		// `onSave` lets a caller redirect persistence elsewhere (e.g. a
		// sub-agent's inline skills map); without it the app-level API is used.
		const result = onSave
			? await onSave(payload, isEdit ? skillId : undefined)
			: isEdit
				? await updatePromptConfigSkill(appId, skillId, payload)
				: await createPromptConfigSkill(appId, payload);

		if (result?.status === 200 || result?.status === 201) {
			onSaved();
		} else {
			setSaveState({ processing: false, failed: true });
		}
	};

	return (
		<Modal
			title={
				<span className="font-semibold text-gray-800">{isEdit ? "Edit Skill" : "Add Skill"}</span>
			}
			open={open}
			onCancel={onClose}
			width={720}
			destroyOnClose
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onClose} disabled={saveState.processing} />
					<Button
						text={isEdit ? "Update" : "Save"}
						onClick={handleSave}
						loading={saveState.processing}
					/>
				</div>
			}
		>
			<div className="flex flex-col gap-4 mt-4 max-h-[70vh] overflow-y-auto pr-1">
				{saveState.failed && <Message text="Failed to save skill. Please try again." type="failure" />}
				{error && <Message text={error} type="failure" />}

				{/* Name */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Skill Name <span className="text-red-500">*</span>
					</label>
					<p className="text-xs text-gray-500 mb-1">
						Lowercase with hyphens, e.g. refund-policy
					</p>
					<input
						type="text"
						value={form.name}
						onChange={(e) => setField("name", e.target.value)}
						placeholder="refund-policy"
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Description */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Description <span className="text-red-500">*</span>
					</label>
					<p className="text-xs text-gray-500 mb-1">
						When should the agent use this skill? This is the only part the agent sees until it
						loads the skill.
					</p>
					<textarea
						value={form.description}
						onChange={(e) => setField("description", e.target.value)}
						rows={2}
						placeholder="Use when the customer asks about refunds, returns or exchanges."
						className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Instructions */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Instructions <span className="text-red-500">*</span>
					</label>
					<p className="text-xs text-gray-500 mb-1">
						Markdown. Loaded only when the agent calls this skill. Use{" "}
						<span className="font-mono">{"{slot_name}"}</span> to insert values collected in the
						conversation.
					</p>
					<textarea
						value={form.body}
						onChange={(e) => setField("body", e.target.value)}
						rows={12}
						placeholder={"# Refund policy\n\n1. Confirm the order id.\n2. Check the order is within 30 days.\n3. ..."}
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Allowed tools */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 mb-1">
						Preferred Tools
					</label>
					<p className="text-xs text-gray-500 mb-1">
						Comma-separated tool names to suggest alongside these instructions. Optional.
					</p>
					<input
						type="text"
						value={form.allowedTools}
						onChange={(e) => setField("allowedTools", e.target.value)}
						placeholder="order_get, refund_create"
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
					/>
				</div>

				{/* Manual only */}
				<div className="pt-1">
					<SwitchComponent
						title="Manual only"
						subTitle="Hide this skill from the agent so it is never invoked automatically"
						enabled={!!form.disableModelInvocation}
						onChange={(state) => setField("disableModelInvocation", state)}
					/>
				</div>
			</div>
		</Modal>
	);
}
